import { amountToDisplayValue, InternalAccessor } from '@billy/design-system'
import { TFunction } from 'i18next'

import { InvoiceDocumentType } from '../../../enums/invoiceDocumentType'
import { InvoiceStatus } from '../../../enums/invoiceStatus'
import type { Invoice } from '../../../types/invoices/invoice'
import { getInvoiceDisplayedType } from '../../../utils/getInvoiceDisplayedType'
import { getInvoiceDisplayedTypeLabel } from '../../../utils/getInvoiceDisplayedTypeLabel'
import { getInvoiceDisplayStatus } from '../../../utils/getInvoiceDisplayStatus'
import { isDocumentTypeCreditNote } from '../../../utils/isDocumentTypeCreditNote'
import { cpaToolsToCegidIntegrationStatus } from '../constants/cpaToolsToCegidIntegrationStatus'
import { InvoicesTableAccessor } from '../enums/invoicesTableAccessor'
import type { InvoicesTableRow } from '../types/invoicesTableRow'
import { getInvoicesTablePaidDate } from './getInvoicesTablePaidDate'
import { getInvoicesTableStaticActions } from './getInvoicesTableStaticActions'

export const getInvoicesTableData = (invoices: Invoice[], t: TFunction): InvoicesTableRow[] => {
  return invoices.map(
    ({
      billingStage,
      creditNoteNumber,
      customer,
      description,
      documentType,
      dueDate,
      id,
      invoiceNumber,
      issueDate,
      referredCreditnoteId,
      locale,
      status,
      summary,
    }) => {
      const { cpaToolsIntegrationStatus, deliveryStatus, eInvoiceStatus = null, status: invoiceStatus } = status ?? {}
      const isCreditNote = isDocumentTypeCreditNote(documentType)
      const displayStatus = getInvoiceDisplayStatus({ referredCreditnoteId, summary, status })
      const isPaidInvoice = invoiceStatus === InvoiceStatus.Paid
      const isVoidedInvoice = invoiceStatus === InvoiceStatus.Deleted
      const isDraft = documentType === InvoiceDocumentType.InvoiceDraft

      const integrationStatus = cpaToolsIntegrationStatus
        ? cpaToolsToCegidIntegrationStatus[cpaToolsIntegrationStatus]
        : undefined

      const paidDate = getInvoicesTablePaidDate({ displayStatus, isCreditNote, paidDate: status?.paidDate })

      const displayedType = getInvoiceDisplayedType({ billingStage, documentType })

      const staticActionsItems = getInvoicesTableStaticActions({
        isCreditNote,
        isDraft,
        invoiceStatus,
        t,
      })

      return {
        [InternalAccessor.StaticActions]: true,
        [InvoicesTableAccessor.Type]: getInvoiceDisplayedTypeLabel(displayedType, t),
        'contact.name': customer.displayName || '',
        billingStage,
        deliveryStatus,
        description: { isCreditNote, value: description },
        documentLocale: locale,
        documentType,
        dueDate,
        eInvoiceStatus,
        id: id.toString(),
        inclVat: `${amountToDisplayValue(summary.foreignGrossTotal)} ${summary.foreignCurrency}`,
        integrationStatus,
        invoiceNo: (isCreditNote ? creditNoteNumber : invoiceNumber) || '—',
        isPaid: isPaidInvoice,
        issueDate,
        isVoided: isVoidedInvoice,
        netTotal: `${amountToDisplayValue(summary.foreignNetTotal)} ${summary.foreignCurrency}`,
        paidDate,
        staticActionsItems,
        status: displayStatus,
      }
    },
  )
}
